import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { COLLECTIVE_SEGMENTS, COLLECTIVE_SOLUTIONS, DESTINATIONS } from "../data/content";

export default function Collective() {
  return (
    <main data-testid="collective-page" className="bg-ivory">
      {/* Hero */}
      <section className="relative h-[100svh] overflow-hidden bg-navy">
        <img src={DESTINATIONS[9].hero} alt="Collective group travel" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.6)" }} />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-[14vh]">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="overline text-gold mb-6">Collective · Group &amp; Institutional Travel</motion.p>
          <h1 className="font-serif text-ivory text-6xl md:text-8xl leading-[0.95] max-w-[14ch]">
            <MaskLines lines={["Travel together.", "Remember forever."]} delay={0.3} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 1 }} className="text-ivory/70 max-w-xl mt-8 text-lg leading-relaxed">
            Expertly managed travel solutions for educational institutions, corporates, organisations and large-scale events — designed with operational precision, safety and seamless execution.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 1 }} className="mt-10">
            <Link to="/enquire?type=collective" data-testid="collective-hero-cta" className="btn-gold">Design Your Group Journey <ArrowRight size={16} /></Link>
          </motion.div>
        </div>
      </section>

      {/* Promise line */}
      <section className="bg-navy py-20" data-testid="collective-promise">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <Reveal><p className="font-serif text-3xl md:text-5xl text-ivory leading-snug">Every movement coordinated. Every detail considered. <span className="text-gold">Every journey delivered with confidence.</span></p></Reveal>
        </div>
      </section>

      {/* Who we serve */}
      <section className="bg-ivory py-28 md:py-40" data-testid="collective-segments">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-16">
            <p className="overline text-charcoal/50">Who We Serve</p>
            <h2 className="font-serif text-4xl md:text-6xl text-navy mt-5">Every group, expertly moved.</h2>
          </Reveal>
          <div className="space-y-4">
            {COLLECTIVE_SEGMENTS.map((s, idx) => (
              <Reveal key={s.name} delay={(idx % 2) * 0.06}>
                <div className={`group grid md:grid-cols-2 gap-8 items-center ${idx % 2 ? "md:[direction:rtl]" : ""}`}>
                  <div className="relative h-[300px] overflow-hidden grain [direction:ltr]">
                    <img src={s.img} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                  </div>
                  <div className="[direction:ltr]">
                    <p className="font-serif text-gold text-xl">0{idx + 1}</p>
                    <h3 className="font-serif text-3xl md:text-4xl text-navy mt-3">{s.name}</h3>
                    <p className="text-charcoal/60 mt-4 leading-relaxed max-w-md">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="bg-navy grain py-28 md:py-40" data-testid="collective-solutions">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-16"><p className="overline text-gold">Collective Solutions</p>
            <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5 max-w-[18ch]">End-to-end execution, from planning to return.</h2></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-ivory/10">
            {COLLECTIVE_SOLUTIONS.map((s, idx) => (
              <Reveal key={s} delay={(idx % 5) * 0.04} className="bg-navy">
                <div className="p-6 h-full flex items-center">
                  <span className="text-gold mr-3">—</span>
                  <span className="text-ivory/80 text-[15px]">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 md:py-48 overflow-hidden bg-navy" data-testid="collective-cta">
        <img src={DESTINATIONS[5].hero} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.72)" }} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-ivory text-5xl md:text-7xl max-w-[22ch] mx-auto leading-[1.05]">It would be a privilege to hear from you.</h2>
            <Link to="/enquire?type=collective" className="btn-gold mt-12" data-testid="collective-bottom-cta">Request a Customized Proposal <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
